<template>
  <div class="main pl-4" v-if="this.availablePhases.length > 0">
    <div class="title mb-3">{{ $t('butlerRanks.title') }}</div>
    <b-container class="filter-section">
      <b-row class="text-center">
        <b-col cols="4" class="filter-label">{{ $t('filters.phase') }}</b-col>
        <b-col cols="4" class="filter-label">{{ $t('filters.serie') }}</b-col>
        <b-col cols="4" class="filter-label">{{ $t('filters.group') }}</b-col>
      </b-row>
      <b-row class="text-center">
        <b-col cols="4">
          <combo-or-label v-model="selectedPhase" :options="availablePhases"></combo-or-label>
        </b-col>
        <b-col cols="4">
          <combo-or-label v-model="selectedSerie" :options="availableSeries"></combo-or-label>
        </b-col>
        <b-col cols="4">
          <combo-or-label v-model="selectedSector" :options="availableSectors"></combo-or-label>
        </b-col>
      </b-row>
      <b-row class="text-center mt-2">
        <b-col cols="4" class="filter-label">{{ $t('filters.session') }}</b-col>
        <b-col cols="4" class="filter-label">{{ $t('filters.round') }}</b-col>
      </b-row>
      <b-row class="text-center">
        <b-col cols="4">
          <combo-or-label v-model="selectedSession" :options="availableSessions"></combo-or-label>
        </b-col>
        <b-col cols="4">
          <combo-or-label v-model="selectedRound" :options="availableRounds"></combo-or-label>
        </b-col>
      </b-row>
    </b-container>

    <b-tabs v-model="activeTab" class="rank">
      <b-tab v-for="[type, rank] in Object.entries(ranksToBeShown)" :key="type"
             :title="rank.RankType === 0 ? $t('rankTabs.session') : $t('rankTabs.cumulative')">
        <b-table :fields="fieldsToBeShown" :items="rank.Rank" small striped class="figbHeader">
          <template #cell(TeamName)="{value}">
            <div>{{ value }}</div>
          </template>
        </b-table>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>

import ComboOrLabel from "../components/lib/ComboOrLabel";
import {mapGetters} from "vuex";

export default {
  name: 'ButlerRanks',
  components: {ComboOrLabel},
  data() {
    return {
      selectedPhase: 1,
      selectedSerie: -1,
      selectedSector: -1,
      selectedSession: -1,
      selectedRound: -1,
      currentDetailsTable: -1,
      activeTab: 0
    }
  },
  computed: {
    ...mapGetters({
      butlerRanks: 'ranks/butlerRanks',
      currentRound: 'tournament/currentRound',
      teamPlayers: 'players/teamPlayers',
      sectors: 'tournament/sector',
    }),
    ranksToBeShown() {
      if (this.selectedRound === -1) {
        return [];
      }

      return this.butlerRanks
          ?.Phases[this.selectedPhase]
          ?.Series[this.selectedSerie]
          ?.Groups[this.selectedSector]
          ?.Sessions[this.selectedSession]
          ?.Rounds[this.selectedRound]
          ?.Ranks ?? {};
    },
    availablePhases() {
      return Object.keys(this.butlerRanks?.Phases ?? {}).sort((a, b) => a - b);
    },
    availableSeries() {
      return Object.keys(
          this.butlerRanks.Phases[this.selectedPhase]?.Series ?? {}
      ).map(seriesId => {
        return {
          value: seriesId,
          text: this.sectors.Phases[this.selectedPhase].Series[seriesId].Sigla
        }
      }).sort((a, b) => a.text.localeCompare(b.text));
    },
    availableSectors() {
      return Object.entries(
          this.butlerRanks
              ?.Phases[this.selectedPhase]
              ?.Series[this.selectedSerie]
              ?.Groups ?? {}
      ).map(([groupId, group]) => {
        const session = Object.keys(group.Sessions)[0];
        return {
          value: groupId,
          text: this.sectors.Phases[this.selectedPhase].Series[this.selectedSerie].Sessions[session].Groups[groupId].Sigla
        }
      }).sort((a, b) => a.text.localeCompare(b.text));
    },
    availableSessions() {
      if (this.selectedSector === -1) {
        return [];
      }
      return Object.keys(
          this.butlerRanks
              ?.Phases[this.selectedPhase]
              ?.Series[this.selectedSerie]
              ?.Groups[this.selectedSector]
              ?.Sessions ?? {}
      ).sort((a, b) => a - b);
    },
    availableRounds() {
      if (this.selectedSession === -1) {
        return [];
      }
      return Object.keys(
          this.butlerRanks
              ?.Phases[this.selectedPhase]
              ?.Series[this.selectedSerie]
              ?.Groups[this.selectedSector]
              ?.Sessions[this.selectedSession]
              ?.Rounds ?? {}
      ).sort((a, b) => a - b);
    },
    fieldsToBeShown() {
      return [
        {
          key: 'Position',
          label: '#'
        },
        {
          key: 'FullName',
          label: this.$t('butlerRanks.playerLabel')
        },
        {
          key: 'Points',
          label: 'Pt.',
          formatter: val => Math.round(val * 100) / 100
        },
        {
          key: 'PlayedBoards',
          label: 'Brd.'
        }
      ];
    }
  },
  methods: {
    openDetails(number) {
      this.currentDetailsTable = number;
    },
    closeDetails() {
      this.openDetails(-1);
    },
    getPlayersOfTeam(teamId) {
      return Object.values(this.teamPlayers).filter(plr => plr.Team === teamId).map(plr => plr.Name)
    }
  },
  watch: {
    selectedSession() {
      this.closeDetails();
    },
    selectedRound() {
      this.closeDetails();
    },
    ranksToBeShown(ranks) {
      const entries = Object.entries(ranks);
      const cumulIdx = entries.findIndex(([, rank]) => rank.RankType !== 0);
      if (cumulIdx !== -1) this.activeTab = cumulIdx;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.selectedPhase = Math.max(...this.availablePhases.map(Number));
      this.$nextTick(() => {
        this.selectedSerie = Math.max(...this.availableSeries.map(option => Number(option.value)));
        this.$nextTick(() => {
          this.selectedSector = Math.max(...this.availableSectors.map(option => Number(option.value)));
          this.$nextTick(() => {
            this.selectedSession = Math.max(...this.availableSessions.map(Number));
            this.$nextTick(() => {
              this.selectedRound = Math.max(...this.availableRounds.map(Number));
            });
          });
        });
      });
    });
  }
};
</script>

<style scoped>
.nsText {
  color: #1d4ed8;
  font-weight: bold;
}

.ewText {
  color: #dc2626;
  font-weight: bold;
}

.impsResult {
  font-size: 2rem;
}

/deep/ .table > tbody > tr > td {
  vertical-align: middle;
}


>>> .figbHeader thead {
  color: #1e3a5f !important;
  background-color: #eef2f7 !important;
}

>>> .rank .nav-link {
  background-color: #f8fafc;
  color: #64748b;
  border-top-right-radius: 0.5rem !important;
  border-top-left-radius: 0.5rem !important;
  border: 1.5px solid transparent;
  transition: background-color 0.15s, color 0.15s;
}

>>> .rank .nav-link.active {
  color: #fff !important;
  font-weight: 700 !important;
  background-color: #1e3a5f !important;
  border-color: #1e3a5f !important;
}

.title {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e3a5f;
  letter-spacing: -0.01em;
}
</style>
